import { useState, useEffect } from 'react';
import { XMarkIcon, UserCircleIcon, CheckCircleIcon, EnvelopeIcon, PencilIcon } from '@heroicons/react/24/outline';
import { supabase, getPlayerAwards, updateRegistrationStatus } from '../../lib/supabase';
import { getSignedUrl } from '../../lib/storage';
import { sendWelcomeEmail, sendStatusChangeEmail, sendPaymentConfirmationEmail } from '../../lib/email';
import { toast } from 'react-hot-toast';

interface Player {
  id: string;
  name: string;
  email: string;
  phone: string;
  club: string;
  handicap: number;
  category: string;
  status: string;
  registrationDate: string;
  paymentStatus: string;
  country: string;
  registrationStatus: { personalInfo: boolean; payment: boolean; verification: boolean };
  payment_proof_url?: string;
  shirt_size?: string;
  registration_number?: string;
  gender?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  player: Player | null;
  tournamentId: string;
}

function statusColor(status: string) {
  switch (status) {
    case 'Active': return 'bg-green-100 text-green-700';
    case 'Pending': return 'bg-amber-100 text-amber-700';
    case 'Withdrawn': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-600';
  }
}

const STATUSES = ['Active', 'Pending', 'Withdrawn'];

export default function MobilePlayerDetailsSheet({ isOpen, onClose, player, tournamentId }: Props) {
  const [awards, setAwards] = useState<any[]>([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [loadingProof, setLoadingProof] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [approvingPayment, setApprovingPayment] = useState(false);
  const [rejectingPayment, setRejectingPayment] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [localPlayer, setLocalPlayer] = useState<Player | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    club: '', handicap: '', category: '', country: '',
    shirt_size: '', gender: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [tournamentCategories, setTournamentCategories] = useState<string[]>([]);

  useEffect(() => {
    if (tournamentId) {
      supabase
        .from('tournament_categories')
        .select('name')
        .eq('tournament_id', tournamentId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })
        .then(({ data }) => {
          if (data && data.length > 0) {
            setTournamentCategories(data.map((c: { name: string }) => c.name));
          }
        });
    }
  }, [tournamentId]);

  useEffect(() => {
    if (isOpen && player) {
      setLocalPlayer(player);
      setIsEditing(false);
      const nameParts = player.name.split(' ');
      setEditForm({
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: player.email || '',
        phone: player.phone || '',
        club: player.club || '',
        handicap: String(player.handicap ?? ''),
        category: player.category || '',
        country: player.country || '',
        shirt_size: player.shirt_size || '',
        gender: (player.gender || '').toLowerCase(),
      });
      fetchAwards();
    }
  }, [isOpen, player]);

  const fetchAwards = async () => {
    if (!player) return;
    try {
      const { data } = await getPlayerAwards(player.id, tournamentId);
      setAwards(data || []);
    } catch {}
  };

  const handleViewProof = async () => {
    if (!localPlayer?.payment_proof_url) return;
    setLoadingProof(true);
    try {
      const url = new URL(localPlayer.payment_proof_url);
      const parts = url.pathname.split('/storage/v1/object/public/');
      if (parts.length < 2) {
        window.location.href = localPlayer.payment_proof_url;
        return;
      }
      const fullPath = parts[1];
      const slashIndex = fullPath.indexOf('/');
      const bucket = fullPath.substring(0, slashIndex);
      const filePath = fullPath.substring(slashIndex + 1);
      const signed = await getSignedUrl(filePath, bucket);
      window.location.href = signed || localPlayer.payment_proof_url;
    } catch {
      window.location.href = localPlayer.payment_proof_url!;
    } finally {
      setLoadingProof(false);
    }
  };

  const handleApprovePayment = async () => {
    if (!localPlayer) return;
    setApprovingPayment(true);
    try {
      const updatedRegStatus = { ...localPlayer.registrationStatus, payment: true };
      const { error } = await updateRegistrationStatus(localPlayer.id, updatedRegStatus);
      if (error) throw error;

      const { data: tournament } = await supabase
        .from('tournaments')
        .select('registration_fee')
        .eq('id', tournamentId)
        .maybeSingle();

      const { data: existing } = await supabase
        .from('financial_transactions')
        .select('id')
        .eq('tournament_id', tournamentId)
        .eq('player_id', localPlayer.id)
        .eq('type', 'income')
        .eq('category', 'registration')
        .maybeSingle();

      if (!existing && tournament) {
        const nameParts = localPlayer.name.split(' ');
        await supabase.from('financial_transactions').insert([{
          tournament_id: tournamentId,
          player_id: localPlayer.id,
          type: 'income',
          category: 'registration',
          description: `Registration fee - ${localPlayer.name}`,
          amount: tournament.registration_fee,
          transaction_date: new Date().toISOString().split('T')[0],
          status: 'completed',
          payment_method: 'Bank Transfer',
          reference_number: localPlayer.registration_number || `REG-${Math.random().toString(36).substring(2, 10).toUpperCase()}`
        }]);
      }

      const nameParts = localPlayer.name.split(' ');
      await sendPaymentConfirmationEmail(
        localPlayer.email,
        nameParts[0],
        nameParts.slice(1).join(' ')
      );

      setLocalPlayer(prev => prev ? { ...prev, paymentStatus: 'Paid', registrationStatus: updatedRegStatus } : prev);
      toast.success('Payment approved and confirmation email sent');
    } catch {
      toast.error('Failed to approve payment');
    } finally {
      setApprovingPayment(false);
    }
  };

  const handleRejectPayment = async () => {
    if (!localPlayer) return;
    setRejectingPayment(true);
    try {
      const updatedRegStatus = { ...localPlayer.registrationStatus, payment: false };
      const { error } = await updateRegistrationStatus(localPlayer.id, updatedRegStatus);
      if (error) throw error;
      setLocalPlayer(prev => prev ? { ...prev, paymentStatus: 'Pending', registrationStatus: updatedRegStatus } : prev);
      toast.success('Payment rejected');
    } catch {
      toast.error('Failed to reject payment');
    } finally {
      setRejectingPayment(false);
    }
  };

  const handleActivateAndEmail = async () => {
    if (!localPlayer) return;
    setSendingEmail(true);
    try {
      const regNumber = localPlayer.registration_number ||
        `IBO-${new Date().getFullYear()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;

      const nameParts = localPlayer.name.split(' ');
      const result = await sendWelcomeEmail(
        localPlayer.email,
        nameParts[0],
        regNumber,
        localPlayer.shirt_size,
        nameParts.slice(1).join(' ')
      );

      if (!result.success) {
        toast.error(result.message || 'Failed to send email');
        return;
      }

      const updatedRegStatus = { ...localPlayer.registrationStatus, verification: true };
      await updateRegistrationStatus(localPlayer.id, updatedRegStatus);

      const { error } = await supabase
        .from('players')
        .update({ status: 'Active', registration_number: regNumber })
        .eq('id', localPlayer.id);
      if (error) throw error;

      setLocalPlayer(prev => prev ? {
        ...prev,
        status: 'Active',
        registration_number: regNumber,
        registrationStatus: updatedRegStatus
      } : prev);
      toast.success('Player activated and welcome email sent');
    } catch {
      toast.error('Failed to activate player');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!localPlayer) return;
    try {
      setUpdatingStatus(true);
      const { error } = await supabase.from('players').update({ status: newStatus }).eq('id', localPlayer.id);
      if (error) throw error;

      const nameParts = localPlayer.name.split(' ');
      await sendStatusChangeEmail(localPlayer.email, nameParts[0], newStatus, nameParts.slice(1).join(' '));

      setLocalPlayer(prev => prev ? { ...prev, status: newStatus } : prev);
      toast.success(`Status updated to ${newStatus}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!localPlayer) return;
    try {
      setDeleting(true);
      const { error } = await supabase.from('players').delete().eq('id', localPlayer.id);
      if (error) throw error;
      toast.success('Player deleted');
      onClose();
    } catch {
      toast.error('Failed to delete player');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!localPlayer) return;
    if (!editForm.firstName.trim() || !editForm.lastName.trim()) {
      toast.error('First and last name are required');
      return;
    }
    setSavingEdit(true);
    try {
      const { error } = await supabase.from('players').update({
        first_name: editForm.firstName.trim(),
        last_name: editForm.lastName.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim(),
        resident_club: editForm.club.trim(),
        handicap: editForm.handicap ? Number(editForm.handicap) : localPlayer.handicap,
        category: editForm.category,
        country: editForm.country.trim(),
        shirt_size: editForm.shirt_size || null,
        gender: editForm.gender || null,
      }).eq('id', localPlayer.id);
      if (error) throw error;
      setLocalPlayer(prev => prev ? {
        ...prev,
        name: `${editForm.firstName.trim()} ${editForm.lastName.trim()}`,
        email: editForm.email.trim(),
        phone: editForm.phone.trim(),
        club: editForm.club.trim(),
        handicap: editForm.handicap ? Number(editForm.handicap) : prev.handicap,
        category: editForm.category,
        country: editForm.country.trim(),
        shirt_size: editForm.shirt_size || undefined,
        gender: editForm.gender || undefined,
      } : prev);
      setIsEditing(false);
      toast.success('Player updated');
    } catch {
      toast.error('Failed to update player');
    } finally {
      setSavingEdit(false);
    }
  };

  if (!isOpen || !localPlayer) return null;

  const row = (label: string, value: string | number | undefined) =>
    value !== undefined && value !== '' ? (
      <div className="flex items-start justify-between gap-4 py-2.5 border-b border-gray-50 last:border-0">
        <p className="text-xs text-gray-400 flex-shrink-0">{label}</p>
        <p className="text-xs font-medium text-golf-gray text-right">{value}</p>
      </div>
    ) : null;

  const isPaid = localPlayer.paymentStatus === 'Paid';

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-golf-gray">{isEditing ? 'Edit Player Info' : 'Player Details'}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{localPlayer.registration_number || localPlayer.name}</p>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <button onClick={() => setIsEditing(true)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-golf-orange/10 active:bg-golf-orange/20 transition-colors">
                <PencilIcon className="w-4 h-4 text-golf-orange" />
              </button>
            )}
            <button onClick={isEditing ? () => setIsEditing(false) : onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
              <XMarkIcon className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {isEditing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">First Name</label>
                  <input type="text" value={editForm.firstName} onChange={e => setEditForm(f => ({ ...f, firstName: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Last Name</label>
                  <input type="text" value={editForm.lastName} onChange={e => setEditForm(f => ({ ...f, lastName: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                <input type="tel" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Resident Club</label>
                <input type="text" value={editForm.club} onChange={e => setEditForm(f => ({ ...f, club: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Handicap</label>
                  <input type="number" value={editForm.handicap} onChange={e => setEditForm(f => ({ ...f, handicap: e.target.value }))}
                    min={0} max={54}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                  <select value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-golf-orange bg-gray-50 [color-scheme:light]">
                    {(tournamentCategories.length > 0 ? tournamentCategories : ['Amateur', 'Professional', 'Senior', 'Junior']).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Gender</label>
                  <select value={editForm.gender} onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-golf-orange bg-gray-50 [color-scheme:light]">
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Shirt Size</label>
                  <select value={editForm.shirt_size} onChange={e => setEditForm(f => ({ ...f, shirt_size: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-golf-orange bg-gray-50 [color-scheme:light]">
                    <option value="">Select</option>
                    {['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Country</label>
                <input type="text" value={editForm.country} onChange={e => setEditForm(f => ({ ...f, country: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50" />
              </div>
              <div className="flex gap-3 pt-1 pb-20">
                <button onClick={() => setIsEditing(false)}
                  className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 bg-white">
                  Cancel
                </button>
                <button onClick={handleSaveEdit} disabled={savingEdit}
                  className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold disabled:opacity-60">
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {!isEditing && (
          <><div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-golf-sand flex items-center justify-center flex-shrink-0">
              <UserCircleIcon className="w-9 h-9 text-golf-gray/40" />
            </div>
            <div>
              <p className="font-bold text-golf-gray">{localPlayer.name}</p>
              <p className="text-xs text-gray-500">{localPlayer.club}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(localPlayer.status)}`}>{localPlayer.status}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isPaid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {localPlayer.paymentStatus}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl px-4">
            {row('Email', localPlayer.email)}
            {row('Phone', localPlayer.phone)}
            {row('Gender', localPlayer.gender ? localPlayer.gender.charAt(0).toUpperCase() + localPlayer.gender.slice(1).toLowerCase() : undefined)}
            {row('Category', localPlayer.category)}
            {row('Handicap', localPlayer.handicap)}
            {row('Shirt Size', localPlayer.shirt_size)}
            {row('Country', localPlayer.country)}
            {row('Registered', localPlayer.registrationDate)}
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Registration Steps</p>
            <div className="flex gap-2">
              {[
                { key: 'personalInfo', label: 'Personal Info' },
                { key: 'payment', label: 'Payment' },
                { key: 'verification', label: 'Verified' },
              ].map(step => (
                <div key={step.key} className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border ${
                  (localPlayer.registrationStatus as any)[step.key] ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                }`}>
                  <CheckCircleIcon className={`w-5 h-5 ${(localPlayer.registrationStatus as any)[step.key] ? 'text-green-600' : 'text-gray-300'}`} />
                  <p className="text-xs text-center font-medium text-gray-600 leading-tight">{step.label}</p>
                </div>
              ))}
            </div>
          </div>

          {localPlayer.payment_proof_url && (
            <button
              onClick={handleViewProof}
              disabled={loadingProof}
              className="w-full py-3 rounded-xl border border-golf-orange/30 text-golf-orange text-sm font-semibold active:bg-golf-orange/5 disabled:opacity-60 transition-colors"
            >
              {loadingProof ? 'Loading...' : 'View Payment Proof'}
            </button>
          )}

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Payment Approval</p>
            <div className="flex gap-2">
              <button
                onClick={handleApprovePayment}
                disabled={approvingPayment || rejectingPayment || isPaid}
                className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                  isPaid
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-green-500 text-white active:bg-green-600'
                }`}
              >
                {approvingPayment ? 'Approving...' : isPaid ? 'Approved' : 'Approve Payment'}
              </button>
              <button
                onClick={handleRejectPayment}
                disabled={approvingPayment || rejectingPayment || !isPaid}
                className="flex-1 py-3 rounded-xl border border-red-200 text-red-500 text-sm font-semibold bg-red-50 active:bg-red-100 disabled:opacity-40 transition-colors"
              >
                {rejectingPayment ? 'Rejecting...' : 'Reject Payment'}
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Activate & Notify</p>
            <button
              onClick={handleActivateAndEmail}
              disabled={sendingEmail || localPlayer.status === 'Active'}
              className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
                localPlayer.status === 'Active'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-golf-orange text-white active:bg-golf-orange/90'
              }`}
            >
              <EnvelopeIcon className="w-4 h-4" />
              {sendingEmail ? 'Sending...' : localPlayer.status === 'Active' ? 'Player is Active' : 'Set Active & Send Welcome Email'}
            </button>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Update Status</p>
            <div className="flex gap-2">
              {STATUSES.map(s => (
                <button
                  key={s}
                  onClick={() => handleStatusChange(s)}
                  disabled={updatingStatus || localPlayer.status === s}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-semibold capitalize transition-colors disabled:opacity-40 ${
                    localPlayer.status === s ? statusColor(s) : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {awards.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Awards</p>
              <div className="space-y-2">
                {awards.map((a: any) => (
                  <div key={a.id} className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                    <p className="text-sm font-semibold text-amber-800">{a.award_categories?.name}</p>
                    {a.notes && <p className="text-xs text-amber-600 mt-0.5">{a.notes}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)} className="w-full py-3 rounded-xl border border-red-200 text-sm font-semibold text-red-500 bg-red-50 active:bg-red-100">
              Delete Player
            </button>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
              <p className="text-sm font-semibold text-red-600">Delete "{localPlayer.name}"?</p>
              <p className="text-xs text-red-500">This cannot be undone.</p>
              <div className="flex gap-2">
                <button onClick={() => setConfirmDelete(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 bg-white">Cancel</button>
                <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold disabled:opacity-60">
                  {deleting ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            </div>
          )}

          <div className="pb-2" />
          </>)}
        </div>
      </div>
    </div>
  );
}
